"use client";

import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import HeaderMap from "./HeaderMap";

export default function Header({
  image,
  title,
  isHome,
  continentCounts,
}: {
  image?:
    | string
    | {
        url?: string;
        thumbs?: {
          md?: string;
          xl?: string;
        };
      }
    | null;
  title?: string | null;
  isHome: boolean;
  continentCounts?: Record<string, number>;
}) {
  const [mounted, setMounted] = useState(false);
  const [showMask, setShowMask] = useState(false);
  const enableMask = showMask;

  useEffect(() => {
    setMounted(true);
    setShowMask(true);
  }, []);

  const overlayImage = isHome
    ? "/assets/pattern-home.svg"
    : "/assets/pattern1.svg";

  const headerHeight = isHome
    ? "h-[45vh] md:h-[70vh] xl:h-[80vh]"
    : "h-[20vh] md:h-[40vh] lg:h-[50vh] xl:h-[65vh]";

  const fallbackImage = isHome
    ? "/assets/image-header-inverse-2.webp"
    : "/assets/image-header-generic.webp";

  const headerImage = useMemo(() => {
    // HOME: wait until mounted so we don't render Kirby image first
    if (isHome && !mounted) {
      return null;
    }

    if (typeof image === "string") return image || fallbackImage;
    if (!image) return fallbackImage;

    return image.thumbs?.xl || image.url || fallbackImage;
  }, [image, fallbackImage, isHome, mounted]);

  return (
    <div
      className={`relative w-full ${headerHeight} flex items-center justify-center overflow-hidden`}
    >
      <div
        className='absolute inset-0 w-full h-full'
        style={
          enableMask
            ? {
                WebkitMaskImage: `url(${overlayImage})`,
                WebkitMaskPosition: "center",
                WebkitMaskSize: "cover",
                WebkitMaskRepeat: "no-repeat",
                maskImage: `url(${overlayImage})`,
                maskPosition: "center",
                maskSize: "cover",
                maskRepeat: "no-repeat",
              }
            : undefined
        }
      >
        {headerImage && (
          <Image
            src={headerImage}
            alt='Header'
            fill
            sizes='100vw'
            style={{
              objectFit: "cover",
              containIntrinsicSize: "100vw 45vh",
            }}
            className='object-cover'
            priority={isHome}
            fetchPriority={isHome ? "high" : "auto"}
            loading={isHome ? "eager" : "lazy"}
            decoding='async'
          />
        )}
        {mounted && isHome && (
          <div className='absolute xl:inset-y-25 inset-y-20 w-full z-10 pointer-events-auto'>
            <HeaderMap continentCounts={continentCounts} />
          </div>
        )}
      </div>

      {title && (
        <div className='absolute inset-0 flex items-center justify-center z-10'>
          <div className='bg-white px-6 py-3 rounded-xl'>
            <h1 className='text-black font-kalam text-3xl md:text-4xl lg:text-5xl font-bold text-center'>
              {title}
            </h1>
          </div>
        </div>
      )}
    </div>
  );
}
